import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config';

export interface User {
  _id?: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'user';
  status: 'active' | 'inactive' | 'resetpassword';
  lastLogin?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  tempPassword?: string;
}

export interface UserResponse {
  statusCode: number;
  message: string;
  data: User;
}

export interface UsersListResponse {
  statusCode: number;
  message: string;
  data: {
    docs: User[];
    totalDocs: number;
    totalPages: number;
    page: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
  };
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password?: string;
  role?: 'admin' | 'manager' | 'user';
  status?: 'active' | 'inactive' | 'resetpassword';
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  role?: 'admin' | 'manager' | 'user';
  status?: 'active' | 'inactive' | 'resetpassword';
}

class UserService {
  /**
   * Get all users with pagination
   */
  async getAllUsers(page: number = 1, limit: number = 10, role?: string): Promise<UsersListResponse> {
    try {
      let endpoint = `/api/users?page=${page}&limit=${limit}`;
      if (role) {
        endpoint += `&role=${role}`;
      }
      const response = await apiClient.get<UsersListResponse['data']>(endpoint, true);
      return {
        statusCode: response.statusCode,
        message: response.message || 'Success',
        data: response.data as UsersListResponse['data'],
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<UserResponse> {
    try {
      const response = await apiClient.get<User>(`/api/users/${id}`, true);
      return {
        statusCode: response.statusCode,
        message: response.message || 'Success',
        data: response.data as User,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new user
   */
  async createUser(data: CreateUserRequest): Promise<UserResponse> {
    try {
      const response = await apiClient.post<User>('/api/users', data, true);
      return {
        statusCode: response.statusCode,
        message: response.message || 'Success',
        data: response.data as User,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user by ID
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<UserResponse> {
    try {
      const response = await apiClient.patch<User>(`/api/users/${id}`, data, true);
      return {
        statusCode: response.statusCode,
        message: response.message || 'Success',
        data: response.data as User,
      };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete user by ID
   */
  async deleteUser(id: string): Promise<any> {
    try {
      const response = await apiClient.delete(`/api/users/${id}`, true);
      return response;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get users by role
   */
  async getUsersByRole(role: string, page: number = 1, limit: number = 10): Promise<UsersListResponse> {
    return this.getAllUsers(page, limit, role);
  }

  /**
   * Handle API errors
   */
  private handleError(error: any): Error {
    if (error instanceof Error) {
      return error;
    }
    return new Error('An unexpected error occurred');
  }
}

export default new UserService();
